/**
 * Phase 8a.2.3 — JS port of `backend/modules/readiness/engine.py`.
 *
 * Pure functions. NO React, NO browser-only APIs. CommonJS so the same file
 * runs in Node (for the engine-parity test) AND ships through CRA's Webpack
 * for the `/admin/scoring-rubric` what-if preview.
 *
 * Only the *numeric* scoring path is ported — the narrative assemblers
 * (next_steps, strengths, weaknesses, blockers) are admin-irrelevant. Output
 * matches `engine.py::calculate()` for these keys:
 *   total_score · tier · funding_path · track_a_score · track_b_score
 *   track_b_applicable · track_a_breakdown · track_b_breakdown
 */

"use strict";

const TIER_FUNDING_READY = "funding_ready";
const TIER_ALMOST_READY = "almost_ready";
const TIER_IMPROVEMENT_RECOMMENDED = "improvement_recommended";
const TIER_REPAIR_REQUIRED = "repair_required";

const PATH_STARTUP = "startup";
const PATH_BUSINESS = "business";

function creditBracketKey(score) {
  if (score >= 750) return "750+";
  if (score >= 720) return "720-749";
  if (score >= 680) return "680-719";
  if (score >= 640) return "640-679";
  if (score >= 600) return "600-639";
  return "<600";
}

function paymentBucketKey(value) {
  if (value === "none_48_months") return "none_48_months";
  if (value === "older_than_48_months") return "older_than_48_months";
  if (value === "one_or_two_recent") return "one_or_two_recent";
  if (value === "multiple_recent") return "multiple_recent";
  return "null";
}

function revenueBracketKey(r) {
  if (r > 20000) return ">20000";
  if (r >= 10000) return "10000-20000";
  if (r >= 5000) return "5000-9999";
  return "0-4999";
}

function trackMaxFor(rubric, trackKey) {
  if (trackKey === "track_a") {
    const cp = rubric.track_a.credit_pts;
    const pp = rubric.track_a.payment_history_pts;
    const fp = rubric.track_a.funding_preparation_pts;
    return {
      credit: Math.max(...Object.values(cp)),
      payment_history: Math.max(...Object.values(pp)),
      funding_preparation: Object.values(fp).reduce((a, b) => a + b, 0),
    };
  }
  const bf = rubric.track_b.business_foundation_pts;
  const bfTotal =
    bf.entity_not_sole_prop +
    bf.has_ein +
    bf.has_business_bank +
    Math.max(...Object.values(bf.time_in_business)) +
    bf.clear_purpose;
  return {
    credit: Math.max(...Object.values(rubric.track_b.credit_pts)),
    payment_history: Math.max(...Object.values(rubric.track_b.payment_history_pts)),
    revenue: Math.max(...Object.values(rubric.track_b.revenue_pts)),
    business_foundation: bfTotal,
  };
}

function computeTier(rubric, score) {
  const th = rubric.tier_thresholds;
  if (score >= th.funding_ready) return TIER_FUNDING_READY;
  if (score >= th.almost_ready) return TIER_ALMOST_READY;
  if (score >= th.improvement_recommended) return TIER_IMPROVEMENT_RECOMMENDED;
  return TIER_REPAIR_REQUIRED;
}

// ----- Track A -------------------------------------------------------------

function effectivePaymentHistory(profile) {
  return profile.effective_payment_history || profile.self_reported_payment_history || null;
}

function computeTrackACredit(rubric, profile) {
  const table = rubric.track_a.credit_pts;
  if (profile.estimated_credit_score_unknown) return table.null || 0;
  const s = profile.estimated_credit_score;
  if (s === null || s === undefined) return table.null || 0;
  return table[creditBracketKey(s)];
}

function computeTrackAPayment(rubric, profile) {
  const table = rubric.track_a.payment_history_pts;
  const key = paymentBucketKey(effectivePaymentHistory(profile));
  return table[key] || 0;
}

function computeTrackAFundingPrep(rubric, profile) {
  const prep = rubric.track_a.funding_preparation_pts;
  let pts = 0;
  const fp = (profile.funding_purpose || "").trim();
  if (fp && fp.toLowerCase() !== "other") pts += prep.clear_purpose || 0;
  if (profile.funding_goal_amount !== null && profile.funding_goal_amount !== undefined && profile.funding_goal_amount > 0) {
    pts += prep.goal_amount_specified || 0;
  }
  return pts;
}

function computeTrackA(rubric, profile) {
  const credit = computeTrackACredit(rubric, profile);
  const payment_history = computeTrackAPayment(rubric, profile);
  const funding_preparation = computeTrackAFundingPrep(rubric, profile);
  return { credit, payment_history, funding_preparation, total: credit + payment_history + funding_preparation };
}

// ----- Track B -------------------------------------------------------------

function isTrackBApplicable(profile) {
  return !!(profile.is_business_owner && profile.has_existing_business);
}

function computeTrackBCredit(rubric, profile) {
  const table = rubric.track_b.credit_pts;
  if (profile.estimated_credit_score_unknown) return table.null || 0;
  const s = profile.estimated_credit_score;
  if (s === null || s === undefined) return table.null || 0;
  return table[creditBracketKey(s)];
}

function computeTrackBPayment(rubric, profile) {
  const table = rubric.track_b.payment_history_pts;
  const key = paymentBucketKey(effectivePaymentHistory(profile));
  return table[key] || 0;
}

function computeTrackBRevenue(rubric, profile) {
  const table = rubric.track_b.revenue_pts;
  const r = profile.monthly_revenue;
  if (r === null || r === undefined || r <= 0) return table.null || 0;
  return table[revenueBracketKey(r)] || 0;
}

function computeTrackBFoundation(rubric, profile) {
  const bf = rubric.track_b.business_foundation_pts;
  const entityOk = ["llc", "corporation", "nonprofit", "other"].includes(profile.business_structure);
  let total = 0;
  total += entityOk ? bf.entity_not_sole_prop : 0;
  total += profile.has_ein ? bf.has_ein : 0;
  total += profile.has_business_bank_account ? bf.has_business_bank : 0;
  const months = profile.time_in_business_months || 0;
  const tib = bf.time_in_business;
  if (months >= 24) total += tib[">=24"];
  else if (months >= 12) total += tib[">=12"];
  else if (months >= 6) total += tib[">=6"];
  else total += tib["<6"];
  const fp = (profile.funding_purpose || "").trim();
  total += fp && fp.toLowerCase() !== "other" ? bf.clear_purpose : 0;
  const cap = trackMaxFor(rubric, "track_b").business_foundation;
  return Math.min(total, cap);
}

function computeTrackB(rubric, profile) {
  if (!isTrackBApplicable(profile)) {
    return { applicable: false, credit: 0, payment_history: 0, revenue: 0, business_foundation: 0, total: 0 };
  }
  const credit = computeTrackBCredit(rubric, profile);
  const payment_history = computeTrackBPayment(rubric, profile);
  const revenue = computeTrackBRevenue(rubric, profile);
  const business_foundation = computeTrackBFoundation(rubric, profile);
  return {
    applicable: true,
    credit,
    payment_history,
    revenue,
    business_foundation,
    total: credit + payment_history + revenue + business_foundation,
  };
}

function calculate(profile, rubric) {
  const track_a = computeTrackA(rubric, profile);
  const track_b = computeTrackB(rubric, profile);
  const a_score = track_a.total;
  const b_score = track_b.applicable ? track_b.total : 0;
  // Ties go to startup, matching engine.py.
  const path = a_score >= b_score ? PATH_STARTUP : PATH_BUSINESS;
  const total_score = Math.max(0, Math.min(Math.max(a_score, b_score), 100));
  const tier = computeTier(rubric, total_score);
  const track_a_breakdown = {
    credit: track_a.credit,
    payment_history: track_a.payment_history,
    funding_preparation: track_a.funding_preparation,
  };
  const track_b_breakdown = track_b.applicable
    ? {
        credit: track_b.credit,
        payment_history: track_b.payment_history,
        revenue: track_b.revenue,
        business_foundation: track_b.business_foundation,
      }
    : null;
  return {
    total_score,
    tier,
    funding_path: path,
    track_a_score: a_score,
    track_b_score: track_b.applicable ? b_score : null,
    track_b_applicable: track_b.applicable,
    track_a_breakdown,
    track_b_breakdown,
  };
}

module.exports = {
  calculate,
  computeTier,
  computeTrackA,
  computeTrackB,
  trackMaxFor,
  isTrackBApplicable,
  PATH_STARTUP,
  PATH_BUSINESS,
  TIER_FUNDING_READY,
  TIER_ALMOST_READY,
  TIER_IMPROVEMENT_RECOMMENDED,
  TIER_REPAIR_REQUIRED,
};
